import { Accordion, AccordionDetails, AccordionSummary, Divider } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { CodeBlock } from 'app/main/test/CodeBlock';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { BadgeChipX } from 'framework/components/chips/BadgeChipX';
import { CollapseX } from 'framework/components/CollapseX';
import { FileDownloadOutlineIcon } from 'framework/components/icons/FileDownloadOutlineIcon';
import { LabelledPropertyX } from 'framework/components/labelledProperty/LabelledPropertyX';
import { NotApplicableEm } from 'framework/components/not-applicable.em';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { Trigger } from 'framework/hooks/useTrigger';
import { isLast } from 'framework/utils/array/isLast';
import { formatDate } from 'framework/utils/date/formatDate';
import { useAccordionState } from 'framework/utils/useAccordionState';
import { documentsQuery_downloadEazyworksDocument, IEazyworksDocument, IEazyworksPatientData, patientsQuery_eazyworks } from 'gen/ApiClient';
import { useDownload } from 'gen/useDownload';
import { useLocalization } from 'localization/useLocalization';
import React, { Fragment } from 'react';

type AccordionType = 'details' | 'blocnotes' | 'documents';

type EazyworksTabProps = {
	patientId: string;
	reloadTrigger: Trigger;
};

export const EazyworksTab = ({ patientId, reloadTrigger }: EazyworksTabProps) => {
	const [item, reload] = useApiEffect(patientsQuery_eazyworks, patientId);

	useLazyEffect(() => {
		reload();
	}, [reloadTrigger]);

	if (!item) {
		return <></>;
	}

	return <EazyworksPatientDataStack item={item} />;
};

type EazyworksPatientDataStackProps = {
	item: IEazyworksPatientData;
};

export const EazyworksPatientDataStack = ({ item }: EazyworksPatientDataStackProps) => {
	const [expandedAccordion, setExpandedAccordion] = useAccordionState<AccordionType>('details');
	const strings = useLocalization();

	return (
		<div className='df-col'>
			<Accordion
				expanded={expandedAccordion === 'details'}
				onChange={() => setExpandedAccordion('details')}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					className='df-row gap-8'>
					<span style={{ fontWeight: 500 }}>{strings.details}</span>
				</AccordionSummary>
				<AccordionDetails className='df-col gap-4'>
					<div style={{ fontWeight: 'bold' }}>{strings.data}</div>
					<LabelledPropertyX label={strings.id}>{item.data.id}</LabelledPropertyX>
					<LabelledPropertyX label={strings.dateCreated}>{formatDate(item.data.dateCreated, 'only-date')}</LabelledPropertyX>
					<LabelledPropertyX label={strings.prescriptionDate}>{formatDate(item.data.prescriptionDate, 'only-date')}</LabelledPropertyX>
					<LabelledPropertyX label={strings.dateLastSale}>{formatDate(item.data.dateLastSale, 'only-date')}</LabelledPropertyX>
					<LabelledPropertyX label={strings.dateEndWarranty}>{formatDate(item.data.endWarrantyDate, 'only-date')}</LabelledPropertyX>
					<div style={{ fontWeight: 'bold' }}>{strings.nawData}</div>
					<LabelledPropertyX label={strings.dateOfBirth}>{formatDate(item.data.dateOfBirth, 'only-date')}</LabelledPropertyX>
					<LabelledPropertyX label={strings.name}>{`${item.data.salutation} ${item.data.firstName} ${item.data.lastName}`}</LabelledPropertyX>
					<LabelledPropertyX label={strings.language}>{item.data.language}</LabelledPropertyX>
					<LabelledPropertyX label={strings.address}>{`${item.data.addressLine} ${item.data.zip} ${item.data.city} ${item.data.country}`}</LabelledPropertyX>
					<CollapseX>
						<CodeBlock value={item.rawData} />
					</CollapseX>
				</AccordionDetails>
			</Accordion>
			<Accordion
				expanded={expandedAccordion === 'blocnotes'}
				onChange={() => setExpandedAccordion('blocnotes')}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					className='df-row gap-8'>
					<span style={{ fontWeight: 500 }}>{`Blocnotes`}</span>
					<BadgeChipX
						value={item.countBlocnotes}
						color={item.countBlocnotes > 0 ? 'error' : 'default'}
						style={{ marginLeft: 8 }}
					/>
				</AccordionSummary>
				<AccordionDetails className='df-col gap-4'>
					{item.blocnotes.map((bn, index) => (
						<Fragment key={index.toString()}>
							<div style={{ whiteSpace: 'pre-line' }}>{bn.content}</div>
							{isLast(bn, item.blocnotes) === false && <Divider />}
						</Fragment>
					))}
					{item.blocnotes.length === 0 && <NotApplicableEm />}
				</AccordionDetails>
			</Accordion>
			<Accordion
				expanded={expandedAccordion === 'documents'}
				onChange={() => setExpandedAccordion('documents')}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					className='df-row gap-8'>
					<span style={{ fontWeight: 500 }}>{strings.documents}</span>
					<BadgeChipX
						value={item.countDocuments}
						color={item.countDocuments > 0 ? 'error' : 'default'}
						style={{ marginLeft: 8 }}
					/>
				</AccordionSummary>
				<AccordionDetails className='df-col gap-4'>
					{item.documents.map(doc => (
						<Fragment key={doc.attachmentId}>
							<DocumentListItem item={doc} />
							{isLast(doc, item.documents) === false && <Divider />}
						</Fragment>
					))}
					{item.documents.length === 0 && <NotApplicableEm />}
				</AccordionDetails>
			</Accordion>
		</div>
	);
};

type DocumentListItemProps = {
	item: IEazyworksDocument;
};

const DocumentListItem = ({ item }: DocumentListItemProps) => {
	const [download, isDownloading] = useDownload(documentsQuery_downloadEazyworksDocument);

	return (
		<TextLinkButton
			onClick={() => download(item.attachmentId, item.documentName ?? '')}
			color='textPrimary'
			underline
			disabled={isDownloading}
			startIcon={<FileDownloadOutlineIcon />}>
			{item.documentName}
		</TextLinkButton>
	);
};
